import * as THREE from 'three';
import { UNIQUES, type UniqueId } from '@somnhold/content';
import { DWOOD, cloth, emis, metal } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The panoply of the old barracks (docs/PANOPLIA.md §9.6): over the armory's rack by the forge, a high row of iron hooks
 * on a dark red cloth, and on them the pieces of the old garrison that Hilde rearmed for the group (twelve at most), each
 * once, glowing by the highest slot stage among its owners. A wooden plaque at the foot counts them in pips: lit for the
 * pieces held, dark for those of the group's classes still missing. Each piece is a low wall version of its card model
 * (a few dozen triangles: a haft and a head, a cuirass, a pendant), all in four materials, so the whole row bakes into
 * four draws and is rebuilt only when what the group holds changes.
 */

export interface PanopliaLook {
  /** The pieces shown, in order, with the stage that lights them (0 to 4). */
  pieces: { id: UniqueId; stage: number }[];
  /** How many of the group's classes' pieces are held, out of how many there are. */
  held: number;
  total: number;
}

/** The row's key: `panoplia:<id.stage,…>|<held>/<total>`. */
export const panopliaKey = (look: PanopliaLook): string => `panoplia:${look.pieces.map((p) => `${p.id}.${p.stage}`).join(',')}|${look.held}/${look.total}`;

/** A key read back (unknown pieces dropped). */
export const parsePanoplia = (s: string | undefined): PanopliaLook => {
  const [list = '', count = '0/0'] = (s ?? '').split('|');
  const [held = '0', total = '0'] = count.split('/');
  return {
    pieces: list
      .split(',')
      .map((x) => x.split('.'))
      .filter(([id]) => !!id && id in UNIQUES)
      .map(([id, st]) => ({ id: id as UniqueId, stage: Math.max(0, Math.min(4, Number(st) || 0)) })),
    held: Number(held) || 0,
    total: Number(total) || 0,
  };
};

// Four materials for the whole row: wood, iron, the dark red cloth, the burning red of runes and pips.
const IRON = metal('#5a6068', { roughness: 0.45, flatShading: true });
const RED = cloth('#5e1c20');
const EMBER = emis('#ff6a50', '#ff2a10', 1.6, { flatShading: true });

/** A piece's wall version, hanging from its hook at the origin (its top), about 0.3 tall. */
const wallPiece = (parent: THREE.Object3D, id: UniqueId): void => {
  const slot = UNIQUES[id].slot;
  const g = G(parent, 0, 0, 0);
  const cls = UNIQUES[id].cls;
  if (slot === 'weapon' && cls === 'ranger') {
    // A bow hung by its string: the limbs' arc, the string, a burning notch at the grip (the quarry's, iron-tipped).
    const bow = M(new THREE.TorusGeometry(0.17, 0.01, 3, 8, Math.PI * 0.8), DWOOD, g);
    bow.position.set(0.03, -0.2, 0);
    bow.rotation.z = Math.PI / 2 + Math.PI * 0.1;
    limb(g, V(0, -0.04, 0), V(0, -0.36, 0), 0.003, 0.003, IRON, 3);
    M(new THREE.BoxGeometry(0.012, 0.04, 0.014), EMBER, g).position.set(0.2, -0.2, 0.01);
    if (id === 'quarryBow') for (const y of [-0.05, -0.35]) M(new THREE.BoxGeometry(0.02, 0.02, 0.02), IRON, g).position.set(0.04, y, 0);
    return;
  }
  if (slot === 'weapon' && cls === 'cleric') {
    // A staff hung by its head: a long pale haft, its top its owner's (a lamp, a bucket, a crescent, a bandage).
    limb(g, V(0, -0.04, 0), V(0, -0.4, 0), 0.01, 0.012, DWOOD, 4);
    if (id === 'lamplighterStaff') M(new THREE.BoxGeometry(0.04, 0.05, 0.04), EMBER, g).position.set(0.04, -0.08, 0);
    else if (id === 'watercarrierStaff') M(new THREE.CylinderGeometry(0.03, 0.025, 0.05, 5), DWOOD, g).position.set(0.05, -0.08, 0);
    else if (id === 'midwifeStaff') M(new THREE.TorusGeometry(0.04, 0.008, 3, 6, Math.PI * 1.3), IRON, g).position.y = -0.02;
    else M(new THREE.CylinderGeometry(0.022, 0.022, 0.06, 5), RED, g).position.y = -0.07;
    M(new THREE.BoxGeometry(0.01, 0.05, 0.014), EMBER, g).position.set(0, -0.22, 0.012);
    return;
  }
  if (slot === 'weapon') {
    // A haft and its head, hung by the head; each piece's head its own.
    limb(g, V(0, -0.06, 0), V(0, -0.36, 0), 0.012, 0.014, DWOOD, 4);
    if (id === 'butcherCleaver') {
      M(new THREE.BoxGeometry(0.14, 0.11, 0.01), IRON, g).position.set(0.05, -0.06, 0);
      M(new THREE.BoxGeometry(0.008, 0.11, 0.012), EMBER, g).position.set(0.12, -0.06, 0);
    } else if (id === 'barracksMace') {
      M(new THREE.CylinderGeometry(0.035, 0.035, 0.1, 6), IRON, g).position.y = -0.05;
      M(new THREE.BoxGeometry(0.1, 0.06, 0.012), IRON, g).position.y = -0.05;
      M(new THREE.BoxGeometry(0.012, 0.06, 0.1), IRON, g).position.y = -0.05;
    } else if (id === 'wellHook') {
      const hook = M(new THREE.TorusGeometry(0.05, 0.01, 3, 6, Math.PI), IRON, g);
      hook.position.set(0.05, -0.38, 0);
      hook.rotation.z = Math.PI;
      M(new THREE.ConeGeometry(0.012, 0.03, 3), EMBER, g).position.set(0.1, -0.37, 0);
    } else if (id === 'hiveStaff') {
      // The druid's staves: a little hive of wood bound in iron, a bunch of herbs, a crook of briars.
      const hive = M(new THREE.SphereGeometry(0.05, 6, 4), DWOOD, g);
      hive.position.y = -0.04;
      hive.scale.y = 1.3;
      M(new THREE.TorusGeometry(0.048, 0.006, 3, 8), IRON, g).position.y = -0.04;
    } else if (id === 'herbalistStaff') {
      for (const k of [-1, 0, 1]) {
        const leaf = M(new THREE.ConeGeometry(0.02, 0.1, 3), RED, g);
        leaf.position.set(k * 0.025, -0.02, 0);
        leaf.rotation.z = Math.PI - k * 0.4;
      }
      M(new THREE.TorusGeometry(0.02, 0.006, 3, 6), IRON, g).position.y = -0.07;
    } else if (id === 'briarStaff') {
      const crook = M(new THREE.TorusGeometry(0.045, 0.01, 3, 6, Math.PI), DWOOD, g);
      crook.position.set(0.045, -0.06, 0);
      for (const [x, y] of [[0.0, -0.1], [0.09, -0.06], [0.03, -0.02]] as const) M(new THREE.ConeGeometry(0.008, 0.03, 3), EMBER, g).position.set(x, y, 0.01);
    } else if (id === 'shepherdAxe' || id === 'brawlerAxe' || id === 'carterAxe') {
      // A greataxe's head: one blade (the brawler's, two), its edge red.
      for (const k of id === 'brawlerAxe' ? [-1, 1] : [1]) {
        M(new THREE.BoxGeometry(0.1, 0.12, 0.01), IRON, g).position.set(k * 0.06, -0.06, 0);
        M(new THREE.BoxGeometry(0.008, 0.12, 0.012), EMBER, g).position.set(k * 0.11, -0.06, 0);
      }
    } else if (id === 'firstRoundHammer' || id === 'penitenceHammer' || id === 'keeperHammer') {
      // A hammer's head across the haft, its face red.
      M(new THREE.BoxGeometry(0.14, 0.06, 0.06), IRON, g).position.y = -0.04;
      M(new THREE.BoxGeometry(0.01, 0.06, 0.062), EMBER, g).position.set(0.07, -0.04, 0);
    } else if (id === 'minerPick') {
      M(new THREE.BoxGeometry(0.2, 0.025, 0.02), IRON, g).position.y = -0.06;
    } else if (id === 'stormStaff' || id === 'distillerStaff' || id === 'pantryStaff') {
      // The mage's staffs: a crystal at the head.
      M(new THREE.OctahedronGeometry(0.035, 0), id === 'pantryStaff' ? IRON : EMBER, g).position.y = -0.05;
    } else M(new THREE.BoxGeometry(0.05, 0.12, 0.02), IRON, g).position.y = -0.06;
    M(new THREE.BoxGeometry(0.01, 0.05, 0.014), EMBER, g).position.set(0, -0.2, 0.012);
  } else if (slot === 'armor') {
    // A small cuirass on a hanger: chest and shoulders, a rune across.
    M(new THREE.BoxGeometry(0.2, 0.012, 0.012), DWOOD, g).position.y = -0.03;
    const chest = M(new THREE.CylinderGeometry(0.1, 0.075, 0.2, 6), IRON, g);
    chest.position.y = -0.14;
    chest.scale.z = 0.6;
    for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.06, 0.035, 0.07), IRON, g).position.set(k * 0.1, -0.05, 0);
    if (id === 'drillPlate') M(new THREE.TorusGeometry(0.04, 0.008, 3, 8), EMBER, g).position.set(0, -0.13, 0.065);
    else if (cls === 'cleric') M(new THREE.BoxGeometry(0.02, 0.18, 0.01), RED, g).position.set(0.03, -0.14, 0.065);
    else if (id === 'trapperJerkin') M(new THREE.TorusGeometry(0.03, 0.007, 3, 6, Math.PI), EMBER, g).position.set(0, -0.2, 0.06);
    else if (id === 'criersCuirass') M(new THREE.CylinderGeometry(0.055, 0.07, 0.04, 6), IRON, g).position.y = -0.03;
    // The druid's: the tunic's poultice pouch, the pelt's fur collar, the gardener's thorned rim.
    else if (id === 'poulticeTunic') M(new THREE.BoxGeometry(0.06, 0.05, 0.03), RED, g).position.set(0.04, -0.18, 0.065);
    else if (id === 'goatherdPelt') {
      const collar = M(new THREE.TorusGeometry(0.07, 0.025, 3, 8), DWOOD, g);
      collar.position.y = -0.05;
      collar.rotation.x = Math.PI / 2;
    }
    else if (id === 'gardenerPlate') for (const k of [-1, 0, 1]) M(new THREE.ConeGeometry(0.01, 0.035, 3), EMBER, g).position.set(k * 0.05, -0.08, 0.07);
    else if (id === 'foremanPelt' || id === 'ancestorPelts') M(new THREE.CylinderGeometry(0.085, 0.1, 0.04, 6), RED, g).position.y = -0.04;
    else if (id === 'challengerHide') {
      const strap = M(new THREE.BoxGeometry(0.02, 0.18, 0.01), EMBER, g);
      strap.position.set(0, -0.14, 0.065);
      strap.rotation.z = 0.5;
    }
    else if (id === 'stairPlates') for (let i = 0; i < 3; i++) M(new THREE.BoxGeometry(0.15 - i * 0.02, 0.02, 0.012), IRON, g).position.set(0, -0.1 - i * 0.045, 0.07);
    else M(new THREE.BoxGeometry(0.12, 0.012, 0.01), EMBER, g).position.set(0, -0.11, 0.065);
  } else {
    // A pendant on its cord: a V of cord and what hangs from it.
    for (const k of [-1, 1]) limb(g, V(0, 0, 0), V(k * 0.04, -0.09, 0), 0.004, 0.004, DWOOD, 3);
    const p = G(g, 0, -0.14, 0);
    if (id === 'menderThimble') M(new THREE.CylinderGeometry(0.03, 0.036, 0.06, 6), IRON, p);
    // The druid's: the undertaker's little spade, the stone of the ground, the orchard's acorn.
    else if (id === 'undertakerPendant') {
      M(new THREE.BoxGeometry(0.05, 0.06, 0.01), IRON, p).position.y = -0.04;
      M(new THREE.BoxGeometry(0.012, 0.06, 0.012), DWOOD, p).position.y = 0.02;
    } else if (id === 'groundStone') M(new THREE.DodecahedronGeometry(0.04, 0), IRON, p);
    else if (id === 'orchardAcorn') {
      M(new THREE.SphereGeometry(0.03, 6, 4), DWOOD, p).position.y = -0.02;
      M(new THREE.SphereGeometry(0.034, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), IRON, p).position.y = 0.0;
    }
    else if (id === 'bullTusk' || id === 'tannerFang') {
      const tooth = M(new THREE.ConeGeometry(id === 'bullTusk' ? 0.025 : 0.015, 0.14, 5), IRON, p);
      tooth.rotation.z = Math.PI + 0.3;
      tooth.position.y = -0.03;
    } else if (id === 'wolfSkull' || id === 'prayerBead') M(new THREE.SphereGeometry(0.045, 6, 4), id === 'prayerBead' ? DWOOD : IRON, p);
    else if (id === 'blessedStone') M(new THREE.BoxGeometry(0.07, 0.08, 0.03), IRON, p);
    else if (id === 'elderCharm') M(new THREE.TorusGeometry(0.045, 0.01, 3, 8), IRON, p);
    else if (id === 'infirmaryBell') M(new THREE.ConeGeometry(0.035, 0.06, 6), IRON, p);
    else if (id === 'ballistaTip' || id === 'archerFeather') {
      const c0 = M(new THREE.ConeGeometry(id === 'ballistaTip' ? 0.035 : 0.02, 0.14, 4), id === 'ballistaTip' ? IRON : RED, p);
      c0.rotation.x = Math.PI;
      c0.position.y = -0.04;
    } else if (id === 'bellNail') {
      M(new THREE.BoxGeometry(0.016, 0.14, 0.016), IRON, p).position.y = -0.04;
      M(new THREE.ConeGeometry(0.03, 0.05, 6), IRON, p).position.set(0.04, 0, 0);
    } else {
      const d = M(new THREE.CylinderGeometry(0.06, 0.06, 0.014, 8), IRON, p);
      d.rotation.x = Math.PI / 2;
    }
    M(new THREE.OctahedronGeometry(0.018, 0), EMBER, p).position.z = 0.02;
  }
};

export const panoplia = (o: BaseOpts & PanopliaLook): Model => {
  const c = begin(o, 0.9, 91);
  const { g } = c;
  const W = 2.5;
  const n = Math.min(12, o.pieces.length);
  // Two tall posts behind the armory's rack and a high beam with its iron hooks; the cloth hangs behind the pieces.
  for (const k of [-1, 1]) limb(g, V(k * (W / 2), 0, -0.4), V(k * (W / 2), 2.62, -0.4), 0.05, 0.055, DWOOD, 5);
  M(new THREE.BoxGeometry(W + 0.24, 0.1, 0.12), DWOOD, g).position.set(0, 2.58, -0.4);
  const cloth = M(new THREE.BoxGeometry(W - 0.12, 0.62, 0.02), RED, g);
  cloth.position.set(0, 2.24, -0.47);
  const step = n > 1 ? (W - 0.36) / (n - 1) : 0;
  for (let i = 0; i < n; i++) {
    const p = o.pieces[i] as PanopliaLook['pieces'][number];
    const x = n > 1 ? -(W - 0.36) / 2 + i * step : 0;
    const hook = M(new THREE.ConeGeometry(0.014, 0.06, 3), IRON, g);
    hook.position.set(x, 2.5, -0.36);
    hook.rotation.x = Math.PI / 2;
    const at = G(g, x, 2.46, -0.34);
    wallPiece(at, p.id);
    // Its slot's stage, as the pieces' own glow does: brighter and wider each stage.
    if (p.stage > 0) glowSprite(at, '#ff4f3a', 0.22 + p.stage * 0.08, V(0, -0.15, 0.06), 0.18 + p.stage * 0.08);
  }
  // The plaque at the left post's foot: one pip for each piece of the group's classes, lit for those held (forty at most).
  const total = Math.min(40, Math.max(o.total, o.held));
  if (total > 0) {
    const cols = Math.min(10, total);
    const rows = Math.ceil(total / cols);
    const plaque = G(g, -W / 2 - 0.05, 0.5, -0.32);
    M(new THREE.BoxGeometry(cols * 0.05 + 0.06, rows * 0.05 + 0.06, 0.03), DWOOD, plaque);
    for (let i = 0; i < total; i++) {
      const pip = M(new THREE.BoxGeometry(0.03, 0.03, 0.012), i < o.held ? EMBER : IRON, plaque);
      pip.position.set(-((cols - 1) * 0.05) / 2 + (i % cols) * 0.05, ((rows - 1) * 0.05) / 2 - Math.floor(i / cols) * 0.05, 0.02);
    }
  }
  return done(c, 2.7);
};
